import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from 'react'
import { isDemoPath } from '../utils/seoDemoPaths';

const DEFAULT_OG_IMAGE = 'https://jazinfotech.com/images/logo/logo.png';
const GSC_VERIFICATION = process.env.REACT_APP_GSC_VERIFICATION || '';

const SEO = ({ title, description, keywords, ogTitle, ogDescription, ogImage, canonical, pageKey, jsonLd, robots, geoRegion = 'IN-TN', geoPlace = 'Tirunelveli' }) => {
    const resolvedRobots = robots ?? (
        typeof window !== 'undefined' && isDemoPath(window.location.pathname)
            ? 'noindex, nofollow'
            : 'index, follow'
    );
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const [fetched, setFetched] = useState(null);
    const [schemaItems, setSchemaItems] = useState([]);

    useEffect(() => {
        let mounted = true;
        async function run() {
            if (!pageKey) return;
            try {
                const res = await fetch(`${API_URL}/api/seo/${encodeURIComponent(pageKey)}?t=${Date.now()}`, { cache: 'no-store' });
                const data = await res.json();
                if (mounted && data.success) setFetched(data.data);
            } catch (e) {
                // ignore and fallback to props/defaults
            }
        }
        run();
        return () => { mounted = false; }
    }, [pageKey, API_URL]);

    useEffect(() => {
        let mounted = true;
        async function loadSchema() {
            if (!pageKey) {
                if (mounted) setSchemaItems([]);
                return;
            }
            try {
                const res = await fetch(`${API_URL}/api/schema?page_key=${encodeURIComponent(pageKey)}&status=active`);
                const data = await res.json();
                if (!mounted || !data.success || !Array.isArray(data.data)) return;
                const parsed = data.data
                    .map((row) => {
                        try {
                            return typeof row.schema_json === 'string' ? JSON.parse(row.schema_json) : row.schema_json;
                        } catch (_) {
                            return null;
                        }
                    })
                    .filter(Boolean);
                setSchemaItems(parsed);
            } catch (_) {
                if (mounted) setSchemaItems([]);
            }
        }
        loadSchema();
        return () => { mounted = false; };
    }, [pageKey, API_URL]);

    const computed = useMemo(() => {
        const fromApi = fetched || {};
        const apiOgTitle = (fromApi.ogTitle ?? fromApi.og_title);
        const apiOgDescription = (fromApi.ogDescription ?? fromApi.og_description);
        const apiOgImage = (fromApi.ogImage ?? fromApi.og_image);
        const apiCanonical = (fromApi.canonical ?? fromApi.canonical_url);

        const siteTitleRaw = (title ?? fromApi.title);
        const siteTitle = siteTitleRaw || '';
        const metaDescription = (description ?? fromApi.description) ?? '';
        const metaKeywords = (keywords ?? fromApi.keywords) ?? '';
        const ogTitleValue = (ogTitle ?? apiOgTitle) ?? siteTitle;
        const ogDescriptionValue = (ogDescription ?? apiOgDescription) ?? metaDescription;
        const ogImageValue = (ogImage ?? apiOgImage) || DEFAULT_OG_IMAGE;
        const canonicalUrl = (canonical ?? apiCanonical) ?? (typeof window !== 'undefined' ? window.location.href.split('?')[0].split('#')[0] : undefined);
        return { siteTitle, metaDescription, metaKeywords, ogTitleValue, ogDescriptionValue, ogImageValue, canonicalUrl };
    }, [title, description, keywords, ogTitle, ogDescription, ogImage, canonical, fetched]);

    const allJsonLd = useMemo(() => {
        const fromProps = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);
        const merged = [...schemaItems, ...fromProps];
        const seen = new Set();
        return merged.filter((item) => {
            const key = item['@id'] || item['@type'] + (item.name || '');
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [jsonLd, schemaItems]);

    useEffect(() => {
        if (typeof document === 'undefined') return;

        if (computed.siteTitle) {
            document.title = computed.siteTitle;
        }

        const upsertMeta = (selector, attrs) => {
            const existing = Array.from(document.head.querySelectorAll(selector));
            existing.forEach(n => n.parentNode && n.parentNode.removeChild(n));
            const el = document.createElement('meta');
            Object.keys(attrs).forEach(k => el.setAttribute(k, attrs[k]));
            document.head.appendChild(el);
        };

        const upsertLink = (rel, href) => {
            if (!href) return;
            const existing = Array.from(document.head.querySelectorAll(`link[rel="${rel}"]`));
            existing.forEach(n => n.parentNode && n.parentNode.removeChild(n));
            const el = document.createElement('link');
            el.setAttribute('rel', rel);
            el.setAttribute('href', href);
            document.head.appendChild(el);
        };

        upsertMeta('meta[name="description"]', { name: 'description', content: computed.metaDescription || '' });
        upsertMeta('meta[name="keywords"]', { name: 'keywords', content: computed.metaKeywords || '' });
        upsertMeta('meta[name="robots"]', { name: 'robots', content: resolvedRobots });
        if (GSC_VERIFICATION) {
            upsertMeta('meta[name="google-site-verification"]', { name: 'google-site-verification', content: GSC_VERIFICATION });
        }
        upsertMeta('meta[name="viewport"]', { name: 'viewport', content: 'width=device-width, initial-scale=1, shrink-to-fit=no' });
        upsertMeta('meta[name="author"]', { name: 'author', content: 'Jaz Infotech' });
        upsertMeta('meta[name="geo.region"]', { name: 'geo.region', content: geoRegion });
        upsertMeta('meta[name="geo.placename"]', { name: 'geo.placename', content: geoPlace });

        upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
        upsertMeta('meta[property="og:title"]', { property: 'og:title', content: computed.ogTitleValue || '' });
        upsertMeta('meta[property="og:description"]', { property: 'og:description', content: computed.ogDescriptionValue || '' });
        upsertMeta('meta[property="og:image"]', { property: 'og:image', content: computed.ogImageValue || '' });
        if (computed.canonicalUrl) {
            upsertMeta('meta[property="og:url"]', { property: 'og:url', content: computed.canonicalUrl });
            upsertLink('canonical', computed.canonicalUrl);
        }
        upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'Jaz Infotech' });
        upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: 'en_IN' });

        upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
        upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: computed.ogTitleValue || '' });
        upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: computed.ogDescriptionValue || '' });
        upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: computed.ogImageValue || '' });

        upsertMeta('meta[name="theme-color"]', { name: 'theme-color', content: '#059DFF' });

        const existingJsonLd = Array.from(document.head.querySelectorAll('script[data-seo-jsonld="true"]'));
        existingJsonLd.forEach(n => n.parentNode && n.parentNode.removeChild(n));
        allJsonLd.forEach(obj => {
            try {
                const script = document.createElement('script');
                script.type = 'application/ld+json';
                script.setAttribute('data-seo-jsonld', 'true');
                script.text = JSON.stringify(obj);
                document.head.appendChild(script);
            } catch (_) { /* noop */ }
        });

    }, [computed, allJsonLd, resolvedRobots, geoRegion, geoPlace]);

    return (
        <>
            <meta charSet="utf-8" />
            <title>{computed.siteTitle}</title>
            <meta name="robots" content={resolvedRobots} />
            {GSC_VERIFICATION ? <meta name="google-site-verification" content={GSC_VERIFICATION} /> : null}
            <meta name="description" content={computed.metaDescription} />
            <meta name="keywords" content={computed.metaKeywords} />
            <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
            <meta name="author" content="Jaz Infotech" />
            <meta name="geo.region" content={geoRegion} />
            <meta name="geo.placename" content={geoPlace} />

            <meta property="og:type" content="website" />
            <meta property="og:title" content={computed.ogTitleValue} />
            <meta property="og:description" content={computed.ogDescriptionValue} />
            <meta property="og:image" content={computed.ogImageValue} />
            <meta property="og:url" content={computed.canonicalUrl} />
            <meta property="og:site_name" content="Jaz Infotech" />
            <meta property="og:locale" content="en_IN" />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={computed.ogTitleValue} />
            <meta name="twitter:description" content={computed.ogDescriptionValue} />
            <meta name="twitter:image" content={computed.ogImageValue} />

            {computed.canonicalUrl ? <link rel="canonical" href={computed.canonicalUrl} /> : null}

            <meta name="theme-color" content="#059DFF" />

            {allJsonLd.map((obj, i) => (
                <script key={i} type="application/ld+json" data-seo-jsonld="true" dangerouslySetInnerHTML={{ __html: JSON.stringify(obj) }} />
            ))}
        </>
    )
}
SEO.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    keywords: PropTypes.string,
    ogTitle: PropTypes.string,
    ogDescription: PropTypes.string,
    ogImage: PropTypes.string,
    canonical: PropTypes.string,
    pageKey: PropTypes.string,
    jsonLd: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    robots: PropTypes.string,
    geoRegion: PropTypes.string,
    geoPlace: PropTypes.string,
};

export default SEO;
